/**
 * Central configuration for all site-wide settings and branding
 * This configuration can be overridden by backend hospital configuration
 */

import apiService, { API_BASE_URL } from '../services/api';
import { getWebsocketBaseUrl } from '../utils/urlConfig';

const resolveApiDisplayBase = () => {
  try {
    const url = new URL(API_BASE_URL);
    return url.origin;
  } catch {
    return API_BASE_URL.replace(/\/api(?:\/)?$/, '');
  }
};

const resolveWebsocketDisplayBase = () => {
  const websocketBase = getWebsocketBaseUrl();
  try {
    const url = new URL(websocketBase);
    return url.origin;
  } catch {
    return websocketBase.replace(/\/ws(?:\/)?$/, '');
  }
};

// Default configuration - Aimepic OneCare defaults
const defaultConfig = {
  // Main site branding
  siteName: 'Aimepic OneCare',
  siteNameShort: 'OneCare',
  siteAbbreviation: 'OneCare',
  
  // Page titles
  titles: {
    main: 'Aimepic OneCare',
    login: 'Login - OneCare',
    dashboard: 'Dashboard - OneCare',
    messages: 'Messages - OneCare',
    settings: 'Settings - OneCare',
    admin: 'Admin Panel - OneCare',
    patient: 'Patient Portal - OneCare',
    staff: 'Staff Dashboard - OneCare',
    aiChat: 'AI Assistant - OneCare',
    notifications: 'Notifications - OneCare',
  },
  
  // Company information
  company: {
    name: 'Aimepic OneCare',
    copyright: '© 2025 Aimepic Healthcare Services',
    tagline: 'Healthcare Operations and Management Platform',
    description: 'Comprehensive healthcare operations and management platform',
  },
  
  // Feature flags
  features: {
    aiChatEnabled: true,
    patentSafeMode: true,
    debugLogsEnabled: false,
    popupAiChatButtonEnabled: false,
  },
  
  // URLs and endpoints
  urls: {
    apiBase: resolveApiDisplayBase(),
    websocketBase: resolveWebsocketDisplayBase(),
    supportEmail: 'admin@aimepic.com',
    website: 'https://aimepic.com',
  },
  
  // Branding colors (can be used for theming)
  branding: {
    primaryColor: '#2563eb',
    secondaryColor: '#059669',
    accentColor: '#dc2626',
    backgroundColor: '#f8f9fa',
    textColor: '#212529',
  },
};

// Export default config
export const siteConfig = defaultConfig;

// Type for site configuration
export type SiteConfig = typeof defaultConfig;

// Dynamic configuration from backend
let dynamicConfig: SiteConfig | null = null;
let configFetchPromise: Promise<SiteConfig> | null = null;

// Function to fetch configuration from backend
export const fetchSiteConfiguration = async (): Promise<SiteConfig> => {
  // Prevent multiple simultaneous fetches
  if (configFetchPromise) {
    if (process.env.REACT_APP_DEBUG_VERBOSE_API === 'true') {
      console.log('[SiteConfig] Already fetching, returning existing promise');
    }
    return configFetchPromise;
  }
  
  // If config is already loaded, return it
  if (dynamicConfig) {
    if (process.env.REACT_APP_DEBUG_VERBOSE_API === 'true') {
      console.log('[SiteConfig] Already loaded, returning cached');
    }
    return dynamicConfig;
  }
  
  // Create the fetch promise
  configFetchPromise = (async () => {
    try {
    if (process.env.REACT_APP_DEBUG_VERBOSE_API === 'true') {
      console.log('[SiteConfig] Fetching config, API:', API_BASE_URL);
    }
    const data = await apiService.get<any>('site-config/');
    
    if (process.env.REACT_APP_DEBUG_VERBOSE_API === 'true') {
      console.log('[SiteConfig] Loaded from site-config/');
    }
    
    // Helper function to get non-empty value or fallback
    const getValueOrDefault = (value: string | undefined | null, fallback: string): string => {
      return (value && value.trim()) ? value.trim() : fallback;
    };
    
    // Map backend response to frontend config structure with proper defaults
    dynamicConfig = {
      siteName: getValueOrDefault(data.site_title, defaultConfig.siteName),
      siteNameShort: getValueOrDefault(data.site_title_short, defaultConfig.siteNameShort),
      siteAbbreviation: getValueOrDefault(data.site_abbreviation, defaultConfig.siteAbbreviation),
      
      titles: {
        main: getValueOrDefault(data.site_title, defaultConfig.titles.main),
        login: getValueOrDefault(data.page_titles?.login, defaultConfig.titles.login),
        dashboard: getValueOrDefault(data.page_titles?.dashboard, defaultConfig.titles.dashboard),
        messages: getValueOrDefault(data.page_titles?.messages, defaultConfig.titles.messages),
        settings: getValueOrDefault(data.page_titles?.settings, defaultConfig.titles.settings),
        admin: getValueOrDefault(data.page_titles?.admin, defaultConfig.titles.admin),
        patient: getValueOrDefault(data.page_titles?.patient, defaultConfig.titles.patient),
        staff: getValueOrDefault(data.page_titles?.staff, defaultConfig.titles.staff),
        aiChat: getValueOrDefault(data.page_titles?.ai_chat, defaultConfig.titles.aiChat),
        notifications: getValueOrDefault(data.page_titles?.notifications, defaultConfig.titles.notifications),
      },
      
      company: {
        name: getValueOrDefault(data.hospital_name, defaultConfig.company.name),
        copyright: `© 2025 ${getValueOrDefault(data.hospital_name, defaultConfig.company.name)}`,
        tagline: getValueOrDefault(data.tagline, defaultConfig.company.tagline),
        description: defaultConfig.company.description,
      },
      
      features: {
        ...defaultConfig.features,
        popupAiChatButtonEnabled: Boolean(data.enable_popup_ai_chat_button),
      },
      urls: defaultConfig.urls,
      
      branding: {
        primaryColor: getValueOrDefault(data.branding?.primary_color, defaultConfig.branding.primaryColor),
        secondaryColor: getValueOrDefault(data.branding?.secondary_color, defaultConfig.branding.secondaryColor),
        accentColor: getValueOrDefault(data.branding?.accent_color, defaultConfig.branding.accentColor),
        backgroundColor: defaultConfig.branding.backgroundColor,
        textColor: defaultConfig.branding.textColor,
      },
    };
    
    // Update the default export
    Object.assign(siteConfig, dynamicConfig);
    
    // Reset the promise after successful completion
    configFetchPromise = null;
    
    return dynamicConfig;
    } catch (error) {
      console.error('Failed to fetch site configuration:', error);
      // Reset the promise on error so it can be retried
      configFetchPromise = null;
      return defaultConfig;
    }
  })();
  
  return configFetchPromise;
};

// Helper function to get current configuration
export const getCurrentConfig = (): SiteConfig => {
  return dynamicConfig || defaultConfig;
};

// Helper function to set document title
export const setDocumentTitle = (pageTitle?: string) => {
  const config = getCurrentConfig();
  
  // Map common page names to their configured titles
  const pageTitleMap: { [key: string]: string } = {
    'Login': config.titles.login,
    'Dashboard': config.titles.dashboard,
    'Messages': config.titles.messages,
    'Settings': config.titles.settings,
    'Admin': config.titles.admin,
    'Patient Portal': config.titles.patient,
    'Staff Dashboard': config.titles.staff,
    'AI Assistant': config.titles.aiChat,
    'Notifications': config.titles.notifications,
  };
  
  if (pageTitle && pageTitleMap[pageTitle]) {
    // Use the configured title for known pages
    document.title = pageTitleMap[pageTitle];
  } else if (pageTitle) {
    // For unknown pages, use the generic format
    document.title = `${pageTitle} - ${config.siteNameShort}`;
  } else {
    // No page title provided, use the main site name
    document.title = config.siteName;
  }
};

// Note: Configuration is now loaded on-demand when fetchSiteConfiguration() is called
// This prevents infinite loops from automatic initialization on module import